import { useState, useRef, useEffect, useCallback, type ReactNode } from 'react';
import { HelpCircle } from 'lucide-react';
import styles from './Tooltip.module.css';

interface Props {
  content: string;
  children?: ReactNode;
  iconSize?: number;
}

export function Tooltip({ content, children, iconSize = 14 }: Props) {
  const [visible, setVisible] = useState(false);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const show = useCallback(() => setVisible(true), []);
  const hide = useCallback(() => setVisible(false), []);

  useEffect(() => {
    if (!visible) return;

    function handleOutside(e: MouseEvent) {
      if (
        popoverRef.current && !popoverRef.current.contains(e.target as Node) &&
        triggerRef.current && !triggerRef.current.contains(e.target as Node)
      ) {
        setVisible(false);
      }
    }

    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setVisible(false);
    }

    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [visible]);

  return (
    <span className={styles.wrapper}>
      <span
        ref={triggerRef}
        className={styles.trigger}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        role="button"
        tabIndex={0}
        aria-describedby={visible ? 'tooltip-popover' : undefined}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setVisible((v) => !v); } }}
      >
        {children ?? <HelpCircle size={iconSize} className={styles.icon} />}
      </span>
      {visible && (
        <div
          ref={popoverRef}
          id="tooltip-popover"
          role="tooltip"
          className={styles.popover}
        >
          {content}
        </div>
      )}
    </span>
  );
}
